import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from 'react-router-dom';
import './App.css';
import LoginPage from './pages/Login/LoginPage';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Container,
  Divider,
  useTheme,
  ListItemButton,
  ThemeProvider,
  createTheme,
  Tooltip,
  Button,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import UserManagementPage from './pages/UserManagement/UserManagementPage';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import TaskManagementPage from './pages/TaskManagement/TaskManagementPage';
import ReportPage from './pages/Report/ReportPage';
import ClientPipelinePage from './pages/ClientPipeline/ClientPipelinePage';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationBell from './components/NotificationBell';

const drawerWidth = 64;

// Create custom theme with red primary color
const theme = createTheme({
  palette: {
    primary: {
      main: '#BA1010',
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const token = localStorage.getItem('token');
  return token ? <>{children}</> : <Navigate to="/login" replace />;
};

const Sidebar: React.FC = () => {
  const location = useLocation();
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: '#f7f9fa',
        },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/user-management"
              selected={location.pathname === '/user-management'}
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                px: 2.5,
                '&:hover': {
                  '& .MuiTooltip-root': {
                    opacity: 1,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 0 }}>
                <Tooltip title="Gestionare Utilizatori" placement="right">
                  <PersonIcon color="primary" />
                </Tooltip>
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/task-management"
              selected={location.pathname === '/task-management'}
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                px: 2.5,
                '&:hover': {
                  '& .MuiTooltip-root': {
                    opacity: 1,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 0 }}>
                <Tooltip title="Oglinda Activitate" placement="right">
                  <AssignmentIcon color="primary" />
                </Tooltip>
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/client-pipeline"
              selected={location.pathname === '/client-pipeline'}
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                px: 2.5,
                '&:hover': {
                  '& .MuiTooltip-root': {
                    opacity: 1,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 0 }}>
                <Tooltip title="Palnie Clienti" placement="right">
                  <BusinessIcon color="primary" />
                </Tooltip>
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/report"
              selected={location.pathname === '/report'}
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                px: 2.5,
                '&:hover': {
                  '& .MuiTooltip-root': {
                    opacity: 1,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: 0 }}>
                <Tooltip title="Raport" placement="right">
                  <AssessmentIcon color="primary" />
                </Tooltip>
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

const Header: React.FC = () => {
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme => theme.zIndex.drawer + 1,
        background: '#fff',
        color: '#222',
        boxShadow: 2,
      }}
    >
      <Toolbar>
        <Box
          component={Link}
          to="/task-management"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            flexGrow: 1,
          }}
        >
          <img
            src="/logo-amass.png"
            alt="Amass Logo"
            style={{ height: 40, marginRight: 16 }}
          />
          <Typography variant="h6" fontWeight={700} noWrap>
            Amass Report
          </Typography>
        </Box>
        <NotificationBell />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderColor: '#BA1010',
            color: '#BA1010',
            '&:hover': {
              borderColor: '#8B0000',
              backgroundColor: 'rgba(186, 16, 16, 0.04)',
            },
          }}
        >
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  );
};

const Footer: React.FC = () => (
  <Box
    sx={{
      textAlign: 'center',
      py: 2,
      color: 'text.secondary',
      background: '#f7f9fa',
      borderTop: '1px solid #eee',
      mt: 4,
    }}
  >
    <Typography variant="body2">
      &copy; {new Date().getFullYear()} Amass Report. All rights reserved.
    </Typography>
  </Box>
);

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f5f6fa' }}>
      <CssBaseline />
      <Header />
      <Sidebar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          mt: 8,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Box sx={{ pt: 1, width: '100%' }}>
          {children}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <UserManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/task-management"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <TaskManagementPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/client-pipeline"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ClientPipelinePage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/report"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ReportPage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/client-pipeline" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
